import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Check, X, Crown, MessageSquare, Sparkles,
} from "lucide-react";
import api, { fmtNaira } from "@/lib/api";
import { toast } from "sonner";

/**
 * Plans page — /app/plans (Iter45)
 * -------------------------------------------------------------
 * Marketing + upgrade page. Three tiers × two billing periods.
 * Annual pricing offers 2 months free (10× monthly) — same discount
 * used across the industry, easy to reason about.
 *
 * The upgrade CTA opens a pre-filled WhatsApp community message
 * (Kobolog Sales) because the Paystack subscription endpoint is not
 * live yet in this environment. Once /subscription/subscribe exists
 * we swap the CTA target back to that endpoint.
 */
const SALES_WA = "https://wa.me/2348020000000?text=" + encodeURIComponent(
  "Hi Kobolog — I'd like to upgrade my plan."
);

const PLANS = [
  {
    key: "starter",
    name: "Starter",
    tagline: "Best for solo traders getting started",
    monthly: 0,
    annual: 0,
    features: [
      "Unlimited text chat bookkeeping",
      "15 voice + photo entries per month",
      "Dashboard, debtors & inventory",
      "1 active loan at a time",
      "Community support",
    ],
    ctaLabel: "You're on Starter",
    highlight: false,
  },
  {
    key: "pro",
    name: "Pro",
    tagline: "For growing traders who want it all",
    monthly: 1500,
    annual: 15000, // 2 months free
    features: [
      "Everything in Starter",
      "Unlimited voice + photo entries",
      "Shareable Daily / Weekly / Monthly PNG reports",
      "Public payment page (@cashtag)",
      "Priority WhatsApp support",
    ],
    ctaLabel: "Upgrade to Pro",
    highlight: true,
  },
  {
    key: "business",
    name: "Business",
    tagline: "For SMEs with growing operations",
    monthly: 5000,
    annual: 50000, // 2 months free
    features: [
      "Everything in Pro",
      "Higher loan limits (₦500k+)",
      "Flexible repayment (weekly / monthly)",
      "Dedicated account manager",
      "Coming soon: multi-staff & API access",
    ],
    ctaLabel: "Upgrade to Business",
    highlight: false,
  },
];

// Feature-by-feature compare grid. Each row: label, then a value per plan key.
const COMPARE_ROWS = [
  { label: "Text-chat bookkeeping", starter: true, pro: true, business: true },
  { label: "Voice + photo entries", starter: "15 / month", pro: "Unlimited", business: "Unlimited" },
  { label: "Dashboard, debtors, inventory", starter: true, pro: true, business: true },
  { label: "Personal reminders (30-second precision)", starter: true, pro: true, business: true },
  { label: "Daily / Weekly / Monthly PNG reports", starter: false, pro: true, business: true },
  { label: "Public @cashtag payment page", starter: false, pro: true, business: true },
  { label: "Loan limit", starter: "₦100k", pro: "₦250k", business: "₦500k+" },
  { label: "Flexible repayment (weekly / monthly)", starter: false, pro: false, business: true },
  { label: "Dedicated account manager", starter: false, pro: false, business: true },
  { label: "Priority WhatsApp support", starter: false, pro: true, business: true },
  { label: "Multi-staff & API (coming soon)", starter: false, pro: false, business: true },
];

const priceLabel = (n, period) => {
  if (n === 0) return "Free";
  return `${fmtNaira(n)}${period === "monthly" ? "/mo" : "/yr"}`;
};

const savingsLabel = (p) => {
  if (p.monthly === 0 || p.annual === 0) return null;
  const saved = p.monthly * 12 - p.annual;
  if (saved <= 0) return null;
  return `Save ${fmtNaira(saved)} / yr`;
};

export default function Plans() {
  const nav = useNavigate();
  const [period, setPeriod] = useState("monthly"); // 'monthly' | 'annual'
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    // Best-effort — endpoint may 404 in preview; we still render the page.
    api.get("/me")
      .then((r) => setCurrent(r.data))
      .catch(() => {});
  }, []);

  const currentKey = (() => {
    if (!current) return "starter";
    if (current.is_premium && current.premium_plan) return current.premium_plan;
    return "starter";
  })();

  const goUpgrade = (plan) => {
    if (currentKey === plan.key) {
      toast.info(`You're already on ${plan.name}.`);
      return;
    }
    // Fallback route until in-app subscription flow is live.
    window.open(SALES_WA, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="px-4 py-5 space-y-6 pb-8" data-testid="plans-page">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => nav(-1)}
          className="w-9 h-9 rounded-full bg-accent flex items-center justify-center"
          data-testid="plans-back"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="font-heading text-xl font-bold" data-testid="plans-title">Choose your plan</h1>
          <p className="text-xs text-kudi-ink/55">One brain across WhatsApp, Web, and Mobile.</p>
        </div>
      </div>

      {/* Current-tier banner */}
      {currentKey !== "starter" && (
        <div className="bg-accent border border-kudi-green/30 rounded-2xl p-4 flex items-center gap-3" data-testid="plans-current-banner">
          <Crown className="w-5 h-5 text-kudi-green" />
          <div>
            <div className="font-heading font-semibold text-sm">
              You're on {currentKey.charAt(0).toUpperCase() + currentKey.slice(1)}
            </div>
            <div className="text-xs text-kudi-ink/60">Thanks for supporting Kobolog 🙏</div>
          </div>
        </div>
      )}

      {/* Monthly / Annual toggle */}
      <div className="flex justify-center">
        <div className="inline-flex bg-white border border-border rounded-full p-1 text-sm" data-testid="plans-period-toggle">
          {["monthly", "annual"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 rounded-full font-medium transition ${
                period === p ? "bg-kudi-green text-white" : "text-kudi-ink/60 hover:text-kudi-ink"
              }`}
              data-testid={`period-${p}`}
            >
              {p === "monthly" ? "Monthly" : "Annual · 2 mo free"}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="space-y-3">
        {PLANS.map((p) => {
          const price = period === "monthly" ? p.monthly : p.annual;
          const isCurrent = currentKey === p.key;
          const savings = period === "annual" ? savingsLabel(p) : null;
          return (
            <div
              key={p.key}
              className={`rounded-2xl p-5 border transition ${
                p.highlight
                  ? "bg-gradient-to-br from-kudi-green to-kudi-green-dark text-white border-transparent shadow-lg"
                  : "bg-white border-border"
              }`}
              data-testid={`plan-card-${p.key}`}
            >
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className={`text-xs font-semibold uppercase tracking-wider ${p.highlight ? "text-white/75" : "text-kudi-ink/55"}`}>
                    {p.highlight ? <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Most popular</span> : p.tagline}
                  </div>
                  <div className="font-heading text-2xl font-bold mt-1 flex items-center gap-2">
                    {p.name}
                    {isCurrent && <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${p.highlight ? "bg-white text-kudi-green" : "bg-kudi-green text-white"}`}>Current</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-3xl font-bold tabular-nums" data-testid={`price-${p.key}`}>
                    {priceLabel(price, period)}
                  </div>
                  {savings && (
                    <div className={`text-[11px] font-medium mt-0.5 ${p.highlight ? "text-amber-200" : "text-kudi-terracotta"}`}>
                      {savings}
                    </div>
                  )}
                </div>
              </div>

              <ul className="mt-4 space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${p.highlight ? "text-white/90" : "text-kudi-ink/75"}`}>
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-white" : "text-kudi-green"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {p.key !== "starter" && (
                <button
                  onClick={() => goUpgrade(p)}
                  disabled={isCurrent}
                  className={`mt-5 w-full rounded-full py-3 font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 ${
                    p.highlight ? "bg-white text-kudi-green hover:bg-kudi-cream" : "bg-kudi-green text-white hover:bg-kudi-green-dark"
                  }`}
                  data-testid={`upgrade-${p.key}`}
                >
                  <MessageSquare className="w-4 h-4" />
                  {isCurrent ? "Current plan" : p.ctaLabel}
                </button>
              )}
              {p.key === "starter" && isCurrent && (
                <div className="mt-5 text-xs text-kudi-ink/55 text-center">
                  You're on the free plan. Upgrade anytime above.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Compare table */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="plans-compare">
        <div className="px-4 py-3 border-b border-border">
          <div className="font-heading font-bold">Compare plans</div>
          <div className="text-xs text-kudi-ink/55">See what's included in each tier.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="bg-accent/40 text-xs uppercase tracking-wider text-kudi-ink/65">
                <th className="text-left px-4 py-3 font-semibold">Feature</th>
                {PLANS.map((p) => (
                  <th key={p.key} className="text-center px-3 py-3 font-semibold">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {COMPARE_ROWS.map((row, i) => (
                <tr key={i}>
                  <td className="px-4 py-3 text-kudi-ink/80">{row.label}</td>
                  {PLANS.map((p) => {
                    const v = row[p.key];
                    return (
                      <td key={p.key} className="text-center px-3 py-3">
                        {v === true ? (
                          <Check className="w-4 h-4 mx-auto text-kudi-green" />
                        ) : v === false ? (
                          <X className="w-4 h-4 mx-auto text-kudi-ink/30" />
                        ) : (
                          <span className="text-xs font-medium text-kudi-ink/70">{v}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-[11px] text-kudi-ink/45">
        Prices in Nigerian Naira. Cancel anytime. Questions? Chat with Kobolog on WhatsApp.
      </div>
    </div>
  );
}
